import React, { PropsWithChildren, ReactNode, memo, useMemo, useRef, useState, MutableRefObject } from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Heading, Select, TextInput } from '@restworld/ui-ds-base';
import { Variable } from '@restworld/utility-types';
import { cn, useIntersectionObserver } from '@restworld/utils-common';

import { LayoutWrapper } from './wrapper-layout';
import { EmployerFiltersModal } from '../components/employer-filters-modal';
import EmployerNoResults from '../components/employer-no-results';
import FallbackSpinner from '../components/fallback-spinner';
import { IEmployerFilterForm } from '../components/job-listing/employer-job-listing-filters';
import { EmployerPrimaryLayoutHeader } from './employer-primary-header';

type LayoutElementProps = {
  element?: ReactNode;
  options?: Variable[];
  title?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  value?: string;
  filterCount?: number;
  content?: string;
  foundNoResults?: boolean;
  formRef?: MutableRefObject<IEmployerFilterForm>;
};

export interface EmployerPrimaryLayoutProps {
  pageTitle?: string;
  addButtonTitle?: string;
  totalSearchResults?: ReactNode;
  filterProps?: LayoutElementProps;
  searchProps?: LayoutElementProps;
  sortProps?: LayoutElementProps;
  noResultsFoundProps?: LayoutElementProps;
  title: string;
  onAddButtonClick?: () => void;
  addButtonLoading?: boolean;
  addButtonDisabled?: boolean;
  addButtonTooltip?: string;
  loadMoreBtnProps?: {
    onClick: () => void;
    loading: boolean;
    hasNextPage: boolean;
  };
  additionalFilters?: ReactNode;
  loading?: boolean;
  onInfoIconClick?: () => void;
  isResultAndFilterElAlwaysOnSameLine?: boolean;
  renderCustomElement?: ReactNode;
}

export function EmployerPrimaryLayout({
  children,
  pageTitle,
  addButtonTitle,
  filterProps,
  searchProps,
  totalSearchResults,
  sortProps,
  noResultsFoundProps,
  title: headTitle,
  onAddButtonClick,
  addButtonLoading,
  addButtonDisabled,
  addButtonTooltip,
  loadMoreBtnProps,
  additionalFilters,
  loading,
  isResultAndFilterElAlwaysOnSameLine,
  onInfoIconClick,
  renderCustomElement,
}: PropsWithChildren<EmployerPrimaryLayoutProps>) {
  const { t: translateCommon } = useTranslation('common');
  const filtersContainerRef = useRef<HTMLDivElement>(null);
  const [isFilterContainerVisible, setIsFilterContainerVisible] = useState(false);

  useIntersectionObserver({
    targetRef: filtersContainerRef,
    options: { threshold: 0.1, rootMargin: '-220px 0px 0px 0px' },
    callback: ([entry]) => setIsFilterContainerVisible(entry.isIntersecting),
  });

  return (
    <LayoutWrapper title={translateCommon(headTitle)} isNavbarOffScreen={!isFilterContainerVisible}>
      <FallbackSpinner />
      <ContentWrapper>
        <ResponsiveContainer>
          <EmployerPrimaryLayoutHeader
            pageTitle={pageTitle}
            addButtonTitle={addButtonTitle}
            onAddButtonClick={onAddButtonClick}
            addButtonLoading={addButtonLoading}
            addButtonDisabled={addButtonDisabled}
            addButtonTooltip={addButtonTooltip}
            onInfoIconClick={onInfoIconClick}
            renderCustomElement={renderCustomElement}
          />
        </ResponsiveContainer>
        <EmployerPrimaryLayoutFilters
          searchProps={searchProps}
          filterProps={filterProps}
          sortProps={sortProps}
          totalSearchResults={totalSearchResults}
          additionalFilters={additionalFilters}
          isFilterContainerVisible={isFilterContainerVisible}
          filtersContainerRef={filtersContainerRef}
          loading={loading}
          isResultAndFilterElAlwaysOnSameLine={isResultAndFilterElAlwaysOnSameLine}
        />
        <ResponsiveContainer>
          <FlexContainer>
            <div className="w-full">
              <EmployerPrimaryLayoutMainContent
                noResultsFoundProps={noResultsFoundProps}
                addButtonTitle={addButtonTitle}
                loadMoreBtnProps={loadMoreBtnProps}
                onAddButtonClick={onAddButtonClick}
                addButtonLoading={addButtonLoading}
                addButtonDisabled={addButtonDisabled}
              >
                {children}
              </EmployerPrimaryLayoutMainContent>
            </div>
          </FlexContainer>
        </ResponsiveContainer>
      </ContentWrapper>
    </LayoutWrapper>
  );
}

export default memo(EmployerPrimaryLayout);

export const EmployerPrimaryLayoutFilters = ({
  searchProps,
  filterProps,
  sortProps,
  totalSearchResults,
  additionalFilters,
  isFilterContainerVisible,
  filtersContainerRef,
  loading,
  isResultAndFilterElAlwaysOnSameLine,
}: Pick<
  EmployerPrimaryLayoutProps,
  | 'searchProps'
  | 'filterProps'
  | 'sortProps'
  | 'totalSearchResults'
  | 'additionalFilters'
  | 'loading'
  | 'isResultAndFilterElAlwaysOnSameLine'
> & {
  isFilterContainerVisible?: boolean;
  filtersContainerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const { t: translateCommon } = useTranslation('common');

  const sortOptions = useMemo(
    () => sortProps?.options?.map((v) => ({ value: v.label, key: v.key, label: v.label })),
    [sortProps?.options]
  );

  return (
    <FiltersWrapper ref={filtersContainerRef} hasBottomBorder={!isFilterContainerVisible}>
      <ResponsiveContainer>
        <Searchbar>
          <TextInput
            id="search"
            icon={MagnifyingGlassIcon}
            bordercolor="gray"
            placeholder={searchProps?.placeholder ? translateCommon(searchProps.placeholder) : ''}
            className="focus:border focus:border-purple-500 focus:outline-none focus:ring-0 focus:shadow-[inset_0_0_0_1px_#A855F7]"
            isFullwidth
            hasLeftPadding
            onChange={(value) => searchProps?.onChange?.(value)}
            value={searchProps?.value}
            disabled={loading}
          />
        </Searchbar>
        <FiltersAndResultsRowWrapper>
          <ResultAndMainFiltersContainer isResultAndFilterElAlwaysOnSameLine={isResultAndFilterElAlwaysOnSameLine}>
            <ResultsContainer>
              <Heading size={6} color="muted-foreground" className="capitalize">
                {translateCommon('results')}:
              </Heading>
              <Heading size={6} className="lowercase">
                {totalSearchResults}
              </Heading>
            </ResultsContainer>
            <FlexContainer>
              {sortProps && (
                <Select
                  options={sortOptions}
                  onChange={(value) => sortProps.onChange?.(value.key)}
                  placeholder={sortProps.placeholder}
                  value={sortOptions?.find((option) => option.key === sortProps.value)}
                  className={'w-[285px]'}
                  tertiaryLabel={`${translateCommon('order_by')}: `}
                />
              )}
              {filterProps && (
                <EmployerFiltersModal filterCount={filterProps?.filterCount} formRef={filterProps?.formRef}>
                  <div className="w-full">{filterProps?.element}</div>
                </EmployerFiltersModal>
              )}
            </FlexContainer>
          </ResultAndMainFiltersContainer>
          {additionalFilters && <AdditionalFiltersWrapper>{additionalFilters}</AdditionalFiltersWrapper>}
        </FiltersAndResultsRowWrapper>
      </ResponsiveContainer>
    </FiltersWrapper>
  );
};

export const EmployerPrimaryLayoutMainContent = ({
  children,
  noResultsFoundProps,
  addButtonTitle,
  onAddButtonClick,
  addButtonLoading,
  addButtonDisabled,
  loadMoreBtnProps,
}: PropsWithChildren<
  Pick<
    EmployerPrimaryLayoutProps,
    | 'noResultsFoundProps'
    | 'addButtonTitle'
    | 'loadMoreBtnProps'
    | 'onAddButtonClick'
    | 'addButtonLoading'
    | 'addButtonDisabled'
  >
>) => {
  const { t: translateCommon } = useTranslation('common');
  return (
    <MainContent>
      {noResultsFoundProps?.foundNoResults ? (
        <EmployerNoResults
          content={translateCommon(noResultsFoundProps.content)}
          title={translateCommon(noResultsFoundProps.title)}
          buttonEl={
            // The empty state repeats the header CTA, so it has to repeat its disabled state
            // too: without it the button looks available while the command refuses the click.
            addButtonTitle ? (
              <ButtonV2
                StartIcon={PlusIcon}
                onClick={onAddButtonClick}
                loading={addButtonLoading}
                disabled={addButtonDisabled}
              >
                {translateCommon(addButtonTitle)}
              </ButtonV2>
            ) : null
          }
        />
      ) : (
        <>
          {children}
          {loadMoreBtnProps.hasNextPage && (
            <div className="flex justify-center pt-16">
              <ButtonV2
                StartIcon={PlusIcon}
                variant="tertiary"
                onClick={loadMoreBtnProps.onClick}
                loading={loadMoreBtnProps.loading}
              >
                {translateCommon('load_more_items')}
              </ButtonV2>
            </div>
          )}
        </>
      )}
    </MainContent>
  );
};

const Searchbar = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <section className={cn('flex justify-between gap-2 w-full py-4 sm:py-8 lg:py-8', className)} {...props} />
);

const ResultAndMainFiltersContainer = ({
  isResultAndFilterElAlwaysOnSameLine,
  className,
  ...props
}: { isResultAndFilterElAlwaysOnSameLine?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <section
    className={cn(
      'flex flex-col gap-3 justify-between w-full md:flex-row md:gap-0',
      isResultAndFilterElAlwaysOnSameLine && 'flex-row',
      className
    )}
    {...props}
  />
);

const MainContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <main className={cn('', className)} {...props} />
);

const ResultsContainer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={className} {...props} />
);

const FlexContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-4 items-end', className)} {...props} />
);
const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('pb-20', className)} {...props} />
);
const ResponsiveContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-4 lg:px-10 xl:px-20 max-w-screen-xl mx-auto', className)} {...props} />
);
// ${'px-4 md:mx-10 md:max-w-[1024px] md:min-w-[640px] xl:max-w-[1280px] xl:min-w-[1024px] xl:mx-20 2xl:max-w-[1280px] 2xl:min-w-[1280px] 2xl:mx-auto'};
const FiltersWrapper = React.forwardRef<
  HTMLDivElement,
  { hasBottomBorder?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ hasBottomBorder, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'w-full pb-[3rem] md:sticky md:top-0 md:bg-neutral-10 md:z-10',
      hasBottomBorder ? 'md:border-b md:border-neutral-40 md:pb-6' : '',
      className
    )}
    {...props}
  />
));
FiltersWrapper.displayName = 'FiltersWrapper';
const AdditionalFiltersWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2 mt-4 min-w-fit md:flex-row md:gap-4 md:mt-0', className)} {...props} />
);
const FiltersAndResultsRowWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col justify-between md:flex-row', className)} {...props} />
);
export * from './employer-primary-header';
